/**
 * RatioDrill — the focused drill for a blended marketing RATIO (MER / aMER /
 * CAC). Founder 2026-06-14: "I clicked into the MER card and it's showing
 * revenue by month" — wrong. A ratio is two totals divided, so the normal
 * breakdown drill (which slices ONE metric by product/state/etc.) can't
 * represent it. This shows the ratio OVER TIME (by month) plus the two sides
 * that drive it, so clicking MER shows MER — not the revenue numerator.
 *
 * Rendered by DrillProvider in place of DrillExplorer when req.ratio is set.
 * A blended ratio can't be sliced by product/state/discount, so there's no
 * filter rail — just the window control (top) and the components table.
 */

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import { Segmented } from "./Segmented.js";
import { TrendChart } from "./TrendChart.js";
import { PERIODS, usePeriod, type PeriodKey } from "../lib/period.js";
import { moneyFmt } from "../lib/format.js";
import type { DrillRequest } from "./DrillExplorer.js";
import { CHANNEL_LABELS } from "../lib/channels.js";

type RatioKey = "mer" | "amer" | "cac" | "spend" | "cm-mer" | "cm-amer";
interface RatioPoint { key: string; value: number | null; numerator: number; denominator: number; incomplete?: boolean }

const x2 = (n: number): string => `${n.toFixed(2)}×`;
const META: Record<RatioKey, { label: string; numLabel: string; denLabel: string; money: boolean; fmt: (n: number) => string; single?: boolean }> = {
  mer: { label: "MER", numLabel: "Revenue", denLabel: "Marketing spend", money: false, fmt: x2 },
  amer: { label: "aMER", numLabel: "New-customer revenue", denLabel: "Acquisition spend", money: false, fmt: x2 },
  cac: { label: "CAC", numLabel: "Acquisition spend", denLabel: "New customers", money: true, fmt: (n) => moneyFmt(n) },
  spend: { label: "Marketing spend", numLabel: "Marketing spend", denLabel: "", money: true, fmt: (n) => moneyFmt(n), single: true },
  "cm-mer": { label: "CM-MER", numLabel: "Contribution margin", denLabel: "Marketing spend", money: false, fmt: x2 },
  "cm-amer": { label: "CM-aMER", numLabel: "New-customer margin", denLabel: "Acquisition spend", money: false, fmt: x2 },
};

export function RatioDrill({ req, onClose, originLabel = "Dashboard" }: { req: DrillRequest; onClose: () => void; originLabel?: string }): React.ReactElement {
  const api = useApiClient();
  const ratio = (req.ratio ?? "mer") as RatioKey;
  const meta = META[ratio];
  const gp = usePeriod();
  // Inherit the card's exact window on open (§18.7). A custom calendar range is
  // already carried on the drill request, so keep those literal dates instead of
  // silently replacing them with one year. The user can still switch to a preset.
  const customWindow = req.period?.from && req.period.to
    ? { from: req.period.from, to: req.period.to, label: req.period.label ?? "Custom range" }
    : null;
  const initKey = customWindow ? "custom" : (req.period?.key ?? gp.key);
  const [winKey, setWinKey] = useState<PeriodKey>(initKey);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  // Channel dropdown (founder 2026-07-14): Blended by default, or one channel's
  // ratio over the same months. Window + channel both refetch.
  const [channel, setChannel] = useState<string>(req.ratioChannel ?? "");
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["ratio-trend", ratio, winKey, customWindow?.from, customWindow?.to, channel],
    queryFn: () => api.get<{ rows: RatioPoint[]; channels?: string[]; coverageNote?: string | null }>("/dashboard/ratio-trend", {
      ratio,
      ...(winKey === "custom" && customWindow
        ? { from: customWindow.from, to: customWindow.to }
        : { period: winKey }),
      ...(channel ? { channel } : {}),
    }),
  });
  const channels = data?.channels ?? [];
  const rows = data?.rows ?? [];
  // M3: the current month is still filling in — keep it in the detail table (with
  // a marker) but drop it from the trend LINE so it doesn't nosedive, unless it's
  // the only point (a short window must never blank).
  const measurable = rows.filter((r): r is RatioPoint & { value: number } => r.value !== null);
  const complete = measurable.filter((r) => !r.incomplete);
  const chartRows = complete.length > 0 ? complete : measurable;
  const hasIncomplete = rows.some((r) => r.incomplete);

  const exportCsv = (): void => {
    const header = ["Month", "Ratio", meta.numLabel, meta.denLabel];
    const lines = [header.join(",")];
    for (const r of rows) lines.push([r.key, r.value === null ? "" : r.value.toFixed(4), String(Math.round(r.numerator * 100) / 100), String(Math.round(r.denominator * 100) / 100)].join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${ratio}-by-month.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={overlay} role="dialog" aria-modal="true">
      <div style={headerBar}>
        <div style={{ minWidth: 0 }}>
          <div style={crumb}>
            <button onClick={onClose} style={crumbLink}>{originLabel}</button>
            <span style={{ color: "var(--faint)" }}> › </span>
            <span style={{ color: "var(--text)", fontWeight: 600 }}>{req.title}</span>
            {isFetching && <span style={{ color: "var(--faint)" }}> · updating…</span>}
          </div>
          <h2 style={{ margin: "2px 0 0", fontSize: 21 }}>{meta.label} by month{channel ? ` · ${CHANNEL_LABELS[channel] ?? channel}` : ""}</h2>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0, flexWrap: "wrap" }}>
          <button onClick={exportCsv} style={ghostBtn}>Export CSV</button>
          <button onClick={onClose} style={closeBtn} aria-label="Close">✕</button>
        </div>
      </div>

      {/* Window control at the top, above the body (founder 2026-06-14). */}
      <div style={windowBar}>
        <span style={railLabel}>Window</span>
        <Segmented size="sm" ariaLabel="Window"
          options={[
            ...(customWindow ? [{ value: "custom" as const, label: customWindow.label }] : []),
            ...PERIODS.map((p) => ({ value: p.value, label: p.label })),
          ]}
          value={winKey} onChange={(v) => setWinKey(v)} />
        {channels.length > 0 && (
          <>
            <span style={{ ...railLabel, marginLeft: 10 }}>Channel</span>
            <select aria-label="Channel" value={channel} onChange={(e) => setChannel(e.target.value)} style={channelSelect}>
              <option value="">Blended (all)</option>
              {channels.map((ch) => <option key={ch} value={ch}>{CHANNEL_LABELS[ch] ?? ch}</option>)}
            </select>
          </>
        )}
      </div>
      {data?.coverageNote && (
        <div style={{ padding: "8px 22px", borderBottom: "1px solid var(--border)", background: "var(--surface-2)", color: "var(--body)", fontSize: 12.5 }}>
          {data.coverageNote}
        </div>
      )}

      <div style={bodyRow}>
        <div style={{ flex: 1, minWidth: 0, padding: "18px 22px", overflowY: "auto" }}>
          {isError ? (
            <div style={centerBox}>
              <div style={{ color: "var(--bad)", fontWeight: 600, marginBottom: 8 }}>Couldn’t load this view.</div>
              <button onClick={() => void refetch()} style={ghostBtn}>Retry</button>
            </div>
          ) : isLoading ? (
            <div style={{ color: "var(--faint)", padding: "40px 0" }}>Loading…</div>
          ) : rows.length === 0 ? (
            <div style={centerBox}><span style={{ color: "var(--faint)" }}>No months with marketing spend in this window.</span></div>
          ) : (
            <>
              <div style={chartCard}>
                <TrendChart kind="line" switchKinds={["line", "area", "step", "stacked-bars", "table", "number"]}
                  chartKey={`ratio-drill-${ratio}`} money={meta.money} showValues valueFmt={meta.fmt}
                  points={chartRows.map((r) => ({ label: r.key, a: r.value }))} />
              </div>
              {hasIncomplete && (
                <div style={{ marginTop: 8, color: "var(--body-faint)", fontSize: 11.5 }}>
                  The current month is still in progress — its spend is counted to date but the month’s sales/customers haven’t all landed, so it’s held out of the trend line (and tagged below). It settles as the month completes.
                </div>
              )}
              {/* Founder 2026-06-22: be explicit that earlier months have sales
                  but no recorded marketing spend, so there's no ratio to plot. */}
              <div style={{ marginTop: 8, color: "var(--body-faint)", fontSize: 11.5 }}>
                Shown from your first month with recorded marketing spend. Earlier months may have sales but no ad/affiliate spend on record — so there’s no {meta.label} to plot for them.
              </div>
              {/* The two sides that drive the ratio, by month. */}
              <div style={{ overflow: "auto", marginTop: 14, border: "1px solid var(--hair)", borderRadius: 8 }}>
                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
                  <thead>
                    <tr>
                      <Th align="left">Month</Th>
                      <Th>{meta.label}</Th>
                      {!meta.single && <Th>{meta.numLabel}</Th>}
                      {!meta.single && <Th>{meta.denLabel}</Th>}
                    </tr>
                  </thead>
                  <tbody>
                    {[...rows].reverse().map((r) => (
                      <tr key={r.key} style={r.incomplete ? { opacity: 0.65 } : undefined}>
                        <td style={tdKey}>{r.key}{r.incomplete ? " · in progress" : ""}</td>
                        <td className="dd-num" style={{ ...td, color: "var(--num)", fontWeight: 700 }}>{r.value === null ? "Not measurable" : meta.fmt(r.value)}</td>
                        {!meta.single && <td className="dd-num" style={td}>{meta.numLabel === "New customers" ? Math.round(r.numerator).toLocaleString() : moneyFmt(r.numerator)}</td>}
                        {!meta.single && <td className="dd-num" style={td}>{meta.denLabel === "New customers" ? Math.round(r.denominator).toLocaleString() : moneyFmt(r.denominator)}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: 10, color: "var(--body-faint)", fontSize: 11.5 }}>
                {meta.single
                  ? "Marketing spend each month = affiliate commissions + your entered ad spend. A blended figure can’t be sliced by product or state."
                  : ratio === "cac"
                    ? "Cost per new customer each month = acquisition spend ÷ new customers. A month with spend but zero new customers stays in the table as Not measurable, so the dollars still reconcile with the headline. You can view it by first-touch channel, but not by product or state."
                    : ratio === "amer" || ratio === "cm-amer"
                      ? "Each month = " + meta.numLabel.toLowerCase() + " ÷ acquisition spend. Channel uses the first touch that acquired each customer."
                      : "Each month = " + meta.numLabel.toLowerCase() + " ÷ marketing spend. MER stays blended because a per-channel MER would assign company-wide revenue too precisely."}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Th({ children, align = "right" }: { children: React.ReactNode; align?: "left" | "right" }): React.ReactElement {
  return (
    <th style={{ position: "sticky", top: 0, background: "var(--surface)", textAlign: align, padding: "9px 12px", fontSize: 11.5, fontWeight: 600, color: "var(--faint)", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap", borderBottom: "1px solid var(--border)" }}>{children}</th>
  );
}

const overlay: React.CSSProperties = { position: "fixed", inset: 0, background: "var(--bg)", zIndex: 100, display: "flex", flexDirection: "column", animation: "dd-fade-in .14s ease" };
const headerBar: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap", padding: "14px 22px", borderBottom: "1px solid var(--border)", background: "var(--surface)" };
const windowBar: React.CSSProperties = { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "10px 22px", borderBottom: "1px solid var(--border)", background: "var(--surface)" };
const crumb: React.CSSProperties = { fontSize: 12, color: "var(--body)" };
const crumbLink: React.CSSProperties = { background: "transparent", border: "none", padding: 0, color: "var(--accent)", fontSize: 12, fontWeight: 600, cursor: "pointer" };
const bodyRow: React.CSSProperties = { display: "flex", flex: 1, minHeight: 0, alignItems: "stretch" };
const chartCard: React.CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-card)", padding: "16px 18px" };
const railLabel: React.CSSProperties = { fontSize: 10.5, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--faint)" };
const channelSelect: React.CSSProperties = { border: "1px solid var(--border)", background: "var(--surface-2)", color: "var(--text)", borderRadius: "var(--r-sm)", padding: "6px 10px", fontSize: 12.5, fontWeight: 600, cursor: "pointer" };
const ghostBtn: React.CSSProperties = { border: "1px solid var(--border)", background: "var(--surface-2)", color: "var(--text)", borderRadius: "var(--r-sm)", padding: "7px 12px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" };
const closeBtn: React.CSSProperties = { border: "1px solid var(--border)", background: "var(--surface-2)", color: "var(--body)", width: 32, height: 32, borderRadius: "var(--r-sm)", cursor: "pointer", fontSize: 13 };
const centerBox: React.CSSProperties = { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, padding: "64px 0" };
const td: React.CSSProperties = { padding: "8px 12px", textAlign: "right", color: "var(--body)", borderBottom: "1px solid var(--hair)", whiteSpace: "nowrap" };
const tdKey: React.CSSProperties = { padding: "8px 12px", textAlign: "left", color: "var(--text)", fontWeight: 500, borderBottom: "1px solid var(--hair)", whiteSpace: "nowrap" };
